import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LogOut, ChevronDown, User, Cloud, Users } from 'lucide-react';
import { LocalizedLink } from '../../components/LocalizedLink';
import { useAuthStore } from '../../stores/authStore';
import { useTranslation } from '../../hooks/useTranslation';
import { useClickOutside } from '../../hooks/useClickOutside';
import { CloudButton } from './CloudButton';
import { ClientOnly } from '../ClientOnly';

export const UserProfile: React.FC = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const user = useAuthStore((state) => state.user);
  const isAdmin = useAuthStore((state) => state.isAdmin);
  const logout = useAuthStore((state) => state.logout);
  const openLoginModal = useAuthStore((state) => state.openLoginModal);
  const menuRef = React.useRef<HTMLDivElement>(null);

  useClickOutside(menuRef, () => setIsOpen(false));

  if (!user) {
    return (
      <motion.div
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className="inline-flex cursor-pointer"
        onClick={openLoginModal}
      >
        <CloudButton variant="profile" isNested={true} />
      </motion.div>
    );
  }

  const handleLogout = async () => {
    setIsOpen(false);
    await logout();
  };

  return (
    <div className="relative" ref={menuRef}>
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 pl-3 pr-4 py-2 rounded-full bg-black/5 hover:bg-black/10 text-gray-900 shadow-sm transition-all duration-200 group"
      >
        {user.photoURL ? (
          <img
            src={user.photoURL}
            alt={user.displayName || 'User'}
            className="w-6 h-6 rounded-full ring-1 ring-white/30 shadow-sm"
          />
        ) : (
          <div className="w-6 h-6 rounded-full flex items-center justify-center ring-1 ring-white/30">
            <User className="w-3 h-3" />
          </div>
        )}
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">
            {user.displayName || t('auth.user')}
          </span>
          <ChevronDown className={`w-4 h-4 text-black/70 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 8 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="absolute right-0 mt-2 w-[260px] z-50"
          >
            <div className="bg-white rounded-xl shadow-lg ring-1 ring-black/5 overflow-hidden">
              {/* Cloud Status Section */}
              <div className="px-3 py-2.5">
                <div className="flex items-center gap-3">
                  <div className="relative flex-shrink-0">
                    <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center">
                      <Cloud className="w-3.5 h-3.5 text-primary-600" />
                    </div>
                    <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-green-500 ring-2 ring-white" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[13px] font-medium text-gray-900 truncate">Cloud Sync Active</div>
                    <div className="text-xs text-gray-500 truncate">Auto-saving changes</div>
                  </div>
                </div>
              </div>

              <div className="mt-1 px-2">
                <div className="bg-gray-50/80 rounded-lg px-3 py-2">
                  <div className="text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                    {t('auth.signedInAs')}
                  </div>
                  <div className="text-xs font-medium text-gray-900 truncate">
                    {user.email}
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="h-[1px] bg-gray-100 my-1.5 mx-2" />

              {/* Admin Link */}
              {isAdmin && (
                <>
                  <div className="px-2">
                    <LocalizedLink
                      to="/admin"
                      onClick={() => setIsOpen(false)}
                      className="w-full flex items-center gap-2.5 px-3 py-1.5 text-sm text-primary-600 hover:text-primary-700 
                               rounded-lg transition-all duration-200 group"
                    >
                      <Users className="w-[15px] h-[15px] text-primary-400 group-hover:text-primary-600 transition-colors duration-200" />
                      <span className="font-medium">{t('admin.menuItem')}</span>
                    </LocalizedLink>
                  </div>
                  <div className="h-[1px] bg-gray-100 my-1.5 mx-2" />
                </>
              )}

              {/* Sign Out Button */}
              <div className="px-2 pb-1">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 
                           rounded-lg transition-all duration-200 group cursor-pointer"
                >
                  <LogOut className="w-[15px] h-[15px] text-gray-400 group-hover:text-gray-600 transition-colors duration-200" />
                  <span className="font-medium">{t('auth.signOut')}</span>
                </motion.div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const ClientOnlyUserProfile: React.FC = () => {
  return (
    <ClientOnly>
      <UserProfile />
    </ClientOnly>
  );
}; 